<template>
  <div>
    <transition-group
      @before-enter="beforeEnter"
      @enter="enter"
      @afterEnter="afterEnter"
      @beforeLeave="beforeLeave"
      @leave="leave"
      @afterLeave="afterLeave"
      class="tag-wrapper"
    >
      <div
        v-for="(child, index) in activeSubsections"
        class="touch"
        :key="child._id"
        :data-index="index"
        @click="navigateToChild(child)"
      >
        <tag-basic
          v-bind="child.marker"
          :pos="{ 
            x: child.marker.x,
            y: child.marker.y
          }"
          :show-sold-label="showSoldLabel(child)"
          :label="{ en: child.label + isSoldOut(child) }"
          :style="`transform: rotate(${child.marker.rotation}deg);`"
        />

        <polygon-shape
          v-if="child.polygon && child.polygon.points.length"
          :polygon="child.polygon"
          :to="polygonUrl(child)"
        />
      </div>
    </transition-group>
  </div>
</template>

<script>
import PolygonShape from '@/components/PolygonShape'
import TagBasic from '@/components/TagBasic'
import { mapState } from 'vuex'
import { bus } from '../main'
import _ from 'lodash'

export default {
  props: ['subsections'],

  components: { PolygonShape, TagBasic },

  data () {
    return {
      activeSubsections: []
    }
  },

  computed: {
    ...mapState({
      inputBaths: state => state.property.inputBaths,
      inputBeds: state => state.property.inputBeds,
      inputCars: state => state.property.inputCars,
      inputPriceRange: state => state.property.inputPriceRange,
      inputLotSizeRange: state => state.property.inputLotSizeRange,
      inputLotNumber: state => state.property.inputLotNumber,
      inputDwellingSizeRange: state => state.property.inputDwellingSizeRange
    }),

    activePropertyId () {
      const propertyId = this.$route.params.propertyId
      return propertyId ? propertyId : null
    },

    styleTemplate () {
      return this.$store.state.styleTemplate
    }
  },

  watch: {
    subsections (newSubsections, oldSubsections) {
      if (!_.isEqual(oldSubsections, newSubsections)) {
        if (oldSubsections.length) {
          this.activeSubsections = []
        }

        if (newSubsections.length) {
          this.$store.commit('addAnimationToQueue', 'LivingOptionSubsections')
        }
      }
    }
  },

  mounted () {
    bus.$on('animateLivingOptionSubsections', () => {
      if (!_.isEqual(this.activeSubsections, this.subsections)) {
        this.activeSubsections = this.subsections
      } else {
        bus.$emit('finishedAnimation')
      }
    })
  },

  methods: {
    navigateToChild (child) {
      const categoryId = this.$route.params.categoryId
      const livingOptionId = this.$route.params.livingOptionId
      const subsectionId = this.$route.params.subsectionId
      const pathway = this.$route.params.pathway

      const activeSubsectionId = this.$store.state.route.query.activeSubsectionId

      const isSoldOut = child.availablePropertiesCount === 0
      const canInteract = this.styleTemplate['general']['can-interact-sold-subsections']

      if (isSoldOut && !canInteract) {
        return
      }

      if (subsectionId) {
        this.$router.push(`/category/${categoryId}/live/${pathway}/${livingOptionId}/subsection/${child._id}${activeSubsectionId ? `?activeSubsectionId=${activeSubsectionId}` : `?activeSubsectionId=${child._id}`}`)
      } else {
        this.$router.push(`${this.$route.path}/subsection/${child._id}${activeSubsectionId ? `?activeSubsectionId=${activeSubsectionId}` : `?activeSubsectionId=${child._id}`}`)
      }
    },

    polygonUrl (child) {
      const categoryId = this.$route.params.categoryId
      const livingOptionId = this.$route.params.livingOptionId
      const subsectionId = this.$route.params.subsectionId
      const pathway = this.$route.params.pathway

      const activeSubsectionId = this.$store.state.route.query.activeSubsectionId

      if (subsectionId) {
        return `/category/${categoryId}/live/${pathway}/${livingOptionId}/subsection/${child._id}${activeSubsectionId ? `?activeSubsectionId=${activeSubsectionId}` : `?activeSubsectionId=${child._id}`}`
      } else {
        return `${this.$route.path}/subsection/${child._id}${activeSubsectionId ? `?activeSubsectionId=${activeSubsectionId}` : `?activeSubsectionId=${child._id}`}`
      }
    },

    isFiltered (subsection) {
      const pathway = this.$store.state.route.params.pathway

      let price 
      if (['apartments'].includes(pathway)) {
        price = subsection.filter.dwellingPriceRange.map(values => values / 1000)
      } else {
        price = subsection.filter.landPriceRange.map(values => values / 1000)
      }

      const bath = subsection.filter.bathRange
      const bed = subsection.filter.bedRange
      const car = subsection.filter.carRange
      const lots = subsection.filter.lotNumbers
      const size = subsection.filter.sizeRange
      const dwellingSize = subsection.filter.dwellingSizeRange

      const inputBaths = this.inputBaths
      const inputBeds = this.inputBeds
      const inputCars = this.inputCars
      const inputLotNumber = this.inputLotNumber
      const inputPriceRange = this.inputPriceRange
      const inputLotSizeRange = this.inputLotSizeRange
      const inputDwellingSizeRange = this.inputDwellingSizeRange

      let isFiltered = false
      
      if (inputBaths && (!bath.includes(inputBaths))) {
        isFiltered = true
      }

      if (inputBeds && (!bed.includes(inputBeds))) {
        isFiltered = true
      }

      if (inputCars && (!car.includes(inputCars))) {
        isFiltered = true
      }

      if (inputLotNumber && (!lots.includes(inputLotNumber))) {
        isFiltered = true
      }

      // confusing logic alert
      // check to see if both the filter low/high ranges fall outside the input ranges
      if (
        !(price[0] >= inputPriceRange[0] && price[0] <= inputPriceRange[1]) &&
        !(price[1] >= inputPriceRange[0] && price[1] <= inputPriceRange[1])
      ) {
        isFiltered = true
      }

      if (
        !(size[0] >= inputLotSizeRange[0] && size[0] <= inputLotSizeRange[1]) &&
        !(size[1] >= inputLotSizeRange[0] && size[1] <= inputLotSizeRange[1])
      ) {
        isFiltered = true
      }

      if (['apartments', 'townhouses'].includes(pathway)) {
        if (
          !(dwellingSize[0] >= inputDwellingSizeRange[0] && dwellingSize[0] <= inputDwellingSizeRange[1]) &&
          !(dwellingSize[1] >= inputDwellingSizeRange[0] && dwellingSize[1] <= inputDwellingSizeRange[1])
        ) {
          isFiltered = true
        }
      }
      
      return isFiltered
    },

    isSoldOut (subsection) {
      return subsection.availablePropertiesCount === 0 ? '' : '<br>now selling'
    },

    showSoldLabel (subsection) {
      // TODO - not used?

      if (this.styleTemplate['general']['show-sold-label'] && subsection.availablePropertiesCount === 0) {
        return true
      } else {
        return false
      }
    },

    beforeEnter (el) {
      el.removeAttribute('style')
      el.style.opacity = 0
    },

    enter (el, done) {
      setTimeout(() => {
        el.style.opacity = 1
        el.style.transition = `opacity 1.5s`
        setTimeout(() => {
          done()
        }, 300)
      }, (parseInt(el.dataset.index) + 1) * 10)
    },

    afterEnter (el) {
      if (parseInt(el.dataset.index) + 1 === this.subsections.length) {
        bus.$emit('finishedAnimation')
      }      
      el.removeAttribute('style')
    },

    beforeLeave (el) {
      el.style.transition = `opacity 0.3s`
    },

    leave (el) {
      el.style.opacity = 0
    },

    afterLeave (el) {
      el.removeAttribute('style')
    }
  }
}
</script>

<style scoped lang="scss">
.tag-wrapper {
  position: absolute;
  top: 0;
  left: 0;

  .layout-switched & {
    top: 10rem;
  }
}
</style>